import { useEffect } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { FieldSyncProvider, SyncBadge } from "./FieldSync";
import { pendingCount, setOwner } from "@/lib/fieldStore";
import artegoIcon from "@/assets/artego-icon.png";

// L'app puo' aprirsi anche senza rete: manifest e service worker propri (scope /campo), come l'app dell'auditor esterno.
function usePwa() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/field-manifest.webmanifest");
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/field-sw.js", { scope: "/campo" }).then(() => navigator.serviceWorker.ready).then((reg) => {
        // dice al service worker quali file della build mettere da parte per l'uso senza rete
        const urls = performance.getEntriesByType("resource").map((r) => r.name).filter((u) => u.startsWith(window.location.origin) && /\/static\/|\.png$|\.webmanifest$/.test(u));
        if (reg.active) reg.active.postMessage({ type: "precache", urls: [...new Set(urls)] });
      }).catch(() => { /* senza service worker l'app funziona comunque in linea */ });
    }
    return () => { if (link && prev) link.setAttribute("href", prev); };
  }, []);
}

// Guscio dell'app dell'auditor interno (dipendente sul campo): vede solo i propri sopralluoghi, anche senza rete.
export default function FieldShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  usePwa();
  if (loading) return <div className="grid min-h-screen place-items-center bg-[#EFE6D8] text-lg">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "auditor") return <Navigate to="/" replace />;
  setOwner(user.id);
  const out = async () => {
    const n = await pendingCount().catch(() => 0);
    if (n > 0 && !window.confirm(`Ci sono ${n} dati non ancora inviati. Restano al sicuro sul tablet e partono al prossimo accesso con la rete. Esci lo stesso?`)) return;
    logout(); navigate("/login");
  };
  return (
    <FieldSyncProvider>
      <div className="min-h-screen bg-[#EFE6D8] text-[#1E2425]" data-testid="field-shell">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-[#D9CBB4] bg-white px-4 py-3">
          <img src={artegoIcon} alt="" className="h-9 w-9 rounded-md object-contain" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-base font-bold leading-tight">Sopralluogo guidato</p>
            <p className="truncate text-xs text-[#5C6667]">{user.name}</p>
          </div>
          <SyncBadge />
          <Button variant="ghost" size="icon" className="h-11 w-11" onClick={out} aria-label="Esci"><LogOut className="h-5 w-5" /></Button>
        </header>
        <main className="mx-auto max-w-3xl px-4 pb-32 pt-4"><Outlet /></main>
      </div>
    </FieldSyncProvider>
  );
}
